import type { Session } from '@supabase/supabase-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

import { staffSupabase } from '../lib/supabase';

export type StaffRole = 'staff' | 'admin';
export type StaffAuthStage = 'password' | 'enroll_mfa' | 'verify_mfa' | 'ready';

export type StaffIdentity = {
  id: string;
  name: string;
  role: StaffRole;
};

export type MfaEnrollment = {
  factorId: string;
  qrCode: string | null;
  secret: string | null;
};

type StaffAuthContextValue = {
  staff: StaffIdentity | null;
  loading: boolean;
  stage: StaffAuthStage;
  mfaEnrollment: MfaEnrollment | null;
  sessionExpiresAt: string | null;
  signIn(email: string, password: string): Promise<string | null>;
  verifyMfa(code: string): Promise<string | null>;
  signOut(): Promise<void>;
};

const StaffAuthContext = createContext<StaffAuthContextValue | null>(null);

function signInFailureMessage(): string {
  // Do not disclose whether the email exists or which security condition was
  // rejected. Details are available to the authenticated audit trail instead.
  return 'Sign-in failed. Check the email address and password.';
}

/**
 * Browser-native staff session and MFA gate.
 *
 * It intentionally uses the admin website's own Supabase client namespace.
 * The server still makes the authorization decision: `register_staff_session`
 * records the twelve-hour absolute lifetime and every staff RPC verifies role,
 * AAL2, and that server-owned session before returning data.
 */
export function StaffAuthProvider({ children }: PropsWithChildren) {
  const [staff, setStaff] = useState<StaffIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState<StaffAuthStage>('password');
  const [mfaEnrollment, setMfaEnrollment] = useState<MfaEnrollment | null>(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<string | null>(null);
  const enrolling = useRef(false);

  const clearLocalState = useCallback(() => {
    setStaff(null);
    setMfaEnrollment(null);
    setSessionExpiresAt(null);
    setStage('password');
  }, []);

  const signOut = useCallback(async () => {
    // Always clear local UI state even if a network failure prevents the
    // remote revocation call from completing. The token will then be rejected
    // when its server-owned expiry is checked by the next protected RPC.
    try {
      await staffSupabase.auth.signOut();
    } finally {
      clearLocalState();
    }
  }, [clearLocalState]);

  const beginMfa = useCallback(async (): Promise<boolean> => {
    // Password sign-in and the auth-state callback can resolve the same session
    // together. The in-flight enrollment will complete the MFA screen for both.
    if (enrolling.current) return true;
    enrolling.current = true;

    try {
      const { data: factors, error: factorError } = await staffSupabase.auth.mfa.listFactors();
      if (factorError) throw factorError;

      const verifiedFactor = factors.totp.find((factor) => factor.status === 'verified');
      if (verifiedFactor) {
        setMfaEnrollment({ factorId: verifiedFactor.id, qrCode: null, secret: null });
        setStage('verify_mfa');
        return true;
      }

      const { data, error: enrollError } = await staffSupabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'FastCloud Admin Website',
      });
      if (enrollError) throw enrollError;

      setMfaEnrollment({
        factorId: data.id,
        qrCode: data.totp.qr_code ?? null,
        secret: data.totp.secret ?? null,
      });
      setStage('enroll_mfa');
      return true;
    } catch {
      await signOut();
      return false;
    } finally {
      enrolling.current = false;
    }
  }, [signOut]);

  const resolveSession = useCallback(
    async (session: Session | null): Promise<'ready' | 'mfa' | 'denied'> => {
      if (!session) {
        clearLocalState();
        return 'denied';
      }

      // This narrow self lookup is intentionally the only staff read at AAL1.
      // All operational data is protected by AAL2 and the server session
      // registry, so a password-only sign-in can only reach the MFA screen.
      const { data: identity, error: identityError } = await staffSupabase
        .from('staff_users')
        .select('id, name, role')
        .eq('id', session.user.id)
        .maybeSingle<StaffIdentity>();

      if (identityError || !identity) {
        await signOut();
        return 'denied';
      }

      const { data: assurance, error: assuranceError } =
        await staffSupabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (assuranceError || assurance.currentLevel !== 'aal2') {
        setStaff(null);
        setSessionExpiresAt(null);
        return (await beginMfa()) ? 'mfa' : 'denied';
      }

      const { data: expiry, error: sessionError } = await staffSupabase.rpc(
        'register_staff_session',
      );
      if (sessionError || !expiry) {
        await signOut();
        return 'denied';
      }

      setMfaEnrollment(null);
      setStaff(identity);
      setSessionExpiresAt(expiry);
      setStage('ready');
      return 'ready';
    },
    [beginMfa, clearLocalState, signOut],
  );

  useEffect(() => {
    let active = true;

    void staffSupabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      await resolveSession(data.session);
      if (active) setLoading(false);
    });

    const {
      data: { subscription },
    } = staffSupabase.auth.onAuthStateChange((_event, nextSession) => {
      // Supabase cautions against awaiting other auth calls inside this event.
      // Deferring keeps the auth lock free while still rechecking a refresh.
      window.setTimeout(() => void resolveSession(nextSession), 0);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [resolveSession]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { data, error } = await staffSupabase.auth.signInWithPassword({ email, password });
      if (error || !data.session) return signInFailureMessage();

      setLoading(true);
      const outcome = await resolveSession(data.session);
      setLoading(false);
      return outcome === 'denied' ? signInFailureMessage() : null;
    },
    [resolveSession],
  );

  const verifyMfa = useCallback(
    async (code: string) => {
      const normalizedCode = code.trim();
      if (!mfaEnrollment || !/^\d{6}$/.test(normalizedCode)) {
        return 'Enter the 6-digit authenticator code.';
      }

      const { error } = await staffSupabase.auth.mfa.challengeAndVerify({
        factorId: mfaEnrollment.factorId,
        code: normalizedCode,
      });
      if (error) return 'That authenticator code was not accepted. Try the current code.';

      const { data } = await staffSupabase.auth.getSession();
      if (!data.session) return 'Your sign-in expired. Please start again.';

      setLoading(true);
      await resolveSession(data.session);
      setLoading(false);
      return null;
    },
    [mfaEnrollment, resolveSession],
  );

  const value = useMemo<StaffAuthContextValue>(
    () => ({
      staff,
      loading,
      stage,
      mfaEnrollment,
      sessionExpiresAt,
      signIn,
      verifyMfa,
      signOut,
    }),
    [loading, mfaEnrollment, sessionExpiresAt, signIn, signOut, staff, stage, verifyMfa],
  );

  return <StaffAuthContext.Provider value={value}>{children}</StaffAuthContext.Provider>;
}

export function useStaffAuth(): StaffAuthContextValue {
  const context = useContext(StaffAuthContext);
  if (!context) {
    throw new Error('useStaffAuth must be used inside StaffAuthProvider.');
  }
  return context;
}
